import { getNewsSummaryForSymbol } from './http/quant-ws/v1/news-summaries';
import { Instant } from '@js-joda/core';
import { ONE_MINUTE } from '@thinkalpha/ui-core/util/timeConstants';

const NEWS_SUMMARY = 'news-summary';

export type ISODateString = string;
export type LookBackPeriod = [ISODateString, ISODateString];

export const getNewsSummaryQuery = (tickerSymbol: string, ...lookBackPeriod: LookBackPeriod) => {
    const [from, to] = lookBackPeriod;

    return {
        queryKey: [NEWS_SUMMARY, tickerSymbol, { start: from, end: to }],
        queryFn: async () =>
            (
                await getNewsSummaryForSymbol(tickerSymbol, {
                    start: Instant.parse(from),
                    end: Instant.parse(to),
                })
            ).summary,
        enabled: !!tickerSymbol,
        refetchInterval: ONE_MINUTE * 5, // refetch every 5 minutes
    };
};

/**
 * Variant of {@link getNewsSummaryQuery} that recomputes `from`/`to` inside
 * the `queryFn` so successive `refetchInterval` invocations always reach back
 * a rolling `windowMs` from the *current* time. Use this when the query is
 * built once and reused across refetches (e.g. behind a long-lived
 * `QueryObserverModel`); the fixed-window factory above is fine when the
 * caller rebuilds the query options on its own cadence.
 *
 * The exact start/end instants are intentionally absent from the query key:
 * they change on every fetch. `windowMs` identifies the rolling range without
 * creating a new cache entry on every refetch.
 */
export const getNewsSummaryRollingQuery = (tickerSymbol: string, windowMs: number) => ({
    queryKey: [NEWS_SUMMARY, tickerSymbol, { rollingWindowMs: windowMs }],
    queryFn: async () => {
        const end = Instant.now();
        const start = end.minusMillis(windowMs);
        return (await getNewsSummaryForSymbol(tickerSymbol, { start, end })).summary;
    },
    enabled: !!tickerSymbol,
    refetchInterval: ONE_MINUTE * 5,
});
